import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckSquareOffset, Warehouse } from '@phosphor-icons/react';
import { Table } from 'antd';
import { tooltipTexts } from '../../../../utility/constants';
import {
  ColumnHeaderTooltip,
  FulfillOrderPopup,
  ItemInfoPopup,
  NameContainer,
  OrderRowActionsDropdown,
  OrderTime,
  PausedOrderColumns,
} from '../../../../page-components';
import {
  CommonConstant,
  CommonUtility,
  DateFormat,
  DateUtility,
  MAX_RESUME_ORDER_IDS,
  OrderService,
  resumePausedOrders,
  theme,
  ToastMessage,
} from '../../../../utility';
import { useGlobalContext } from '../../../../contexts';
import { useOrdersTabExtraContent } from '@contexts/orders-tab-extra';
import { OrderHook, useDebouncedMemo } from '@hooks/index';
import { OrdersTabToolbar } from '@page-components/order/orders-tab-toolbar';
import { AlertFooterBar } from '../../../../elements/FooterAlert';
import { MuteText } from '../../../../elements';
import { OrganisationAvatar } from '../../../../components/ImageAvatar';

function getProvider(order) {
  return order?.items?.[0]?.providerDetails?.[0];
}

function getTotalItems(order) {
  return (order?.items || []).reduce((total, item) => total + item.qty, 0);
}

function getAntdSortOrder(sortDirection) {
  if (sortDirection === CommonConstant.sortDirection.ascending) {
    return 'ascend';
  }
  if (sortDirection === CommonConstant.sortDirection.descending) {
    return 'descend';
  }
  return null;
}

function getSortDirection(order) {
  return order === 'ascend'
    ? CommonConstant.sortDirection.ascending
    : CommonConstant.sortDirection.descending;
}

export function OrderListTab() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { warehouseId } = useGlobalContext();

  const currentTab = useMemo(() => {
    if (pathname) {
      return CommonUtility.getSegmentByPosition(pathname, 4);
    }
    return null;
  }, [pathname]);

  const [loading, setLoading] = useState(false);
  const [recalculatingPaused, setRecalculatingPaused] = useState(false);
  const [recalculatingOrderId, setRecalculatingOrderId] = useState(null);
  const [selectedById, setSelectedById] = useState({});

  const [orderToFulfill, setOrderToFulfill] = useState(null);

  const [searchParams] = useSearchParams();

  const [customerId, setCustomerId] = useState('');

  const {
    refresh,
    filter: tableFilter,
    filterChanged,
    loading: orderLoading,
    data: orderData,
    totalData,
    pageChanged,
    parsedSort,
    updateAllFilter,
    onSortChange,
  } = OrderHook({ type: currentTab, customerId });

  const fulfillOrder = async () => {
    try {
      setLoading(true);
      const payload = {
        action: 'fulfill',
      };

      await OrderService.orderAction(orderToFulfill, payload);
      closePopup();
      ToastMessage.success('Order fulfilled successfully.');
      refresh();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  const openPopup = useCallback(order => {
    setOrderToFulfill(order._id);
  }, []);

  const closePopup = useCallback(() => {
    setOrderToFulfill(null);
  }, []);

  const isPausedTab = currentTab === 'paused';

  const selectedOrders = useMemo(() => Object.values(selectedById), [selectedById]);
  const selectedCount = selectedOrders.length;

  const onBulkSelectToggle = useCallback((order, checked) => {
    setSelectedById(prev => {
      if (!checked) {
        const next = { ...prev };
        delete next[order._id];
        return next;
      }
      if (Object.keys(prev).length >= MAX_RESUME_ORDER_IDS) {
        ToastMessage.error(
          `You can select up to ${MAX_RESUME_ORDER_IDS} orders at a time.`,
        );
        return prev;
      }
      return { ...prev, [order._id]: order };
    });
  }, []);

  const clearBulkSelection = useCallback(() => {
    setSelectedById({});
  }, []);

  const bulkSelectableOrders = useMemo(
    () => (orderData || []).slice(0, MAX_RESUME_ORDER_IDS),
    [orderData],
  );

  const onBulkSelectHeaderToggle = useCallback(
    checked => {
      if (!checked) {
        setSelectedById({});
        return;
      }

      const nextSelected = {};
      bulkSelectableOrders.forEach(order => {
        if (order?._id) {
          nextSelected[order._id] = order;
        }
      });
      setSelectedById(nextSelected);
    },
    [bulkSelectableOrders],
  );

  const recalculatePausedFulfillment = useCallback(
    async orderId => {
      const orderIds = orderId ? [orderId] : selectedOrders.map(order => order._id);

      try {
        if (orderId) {
          setRecalculatingOrderId(orderId);
        } else {
          setRecalculatingPaused(true);
        }

        const result = await resumePausedOrders({ warehouseId, orderIds });
        if (result.ok) {
          if (!orderId) {
            setSelectedById({});
          }
          refresh();
        }
      } finally {
        setRecalculatingPaused(false);
        setRecalculatingOrderId(null);
      }
    },
    [warehouseId, refresh, selectedOrders],
  );

  const ageSortOrder = getAntdSortOrder(parsedSort.timeAcknowledged);

  const standardOrderColumns = useMemo(
    () => [
      {
        title: <ColumnHeaderTooltip label="Order ID" helpText={tooltipTexts.orderId} />,
        key: 'order_id',
        align: 'left',
        width: '16%',
        render: (_, order) => {
          const provider = getProvider(order);

          return (
            <div className="d-flex align-items-start">
              <div>
                {provider ? (
                  <Link
                    to={`/app/fulfill/orders/details/${order._id}/?providerId=${provider?._id}`}
                  >
                    {order.marketplaceOrderDisplayId}
                  </Link>
                ) : (
                  order.marketplaceOrderDisplayId
                )}
              </div>
            </div>
          );
        },
      },
      {
        title: <ColumnHeaderTooltip label="Age" helpText={tooltipTexts.orderAge} />,
        key: 'timeAcknowledged',
        align: 'left',
        width: '14%',
        sorter: true,
        sortOrder: ageSortOrder,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, order) =>
          order.timeAcknowledged ? (
            <>
              <OrderTime time={order.timeAcknowledged} isEnabled />
              <div>
                <MuteText>
                  {DateUtility.formatDate(
                    order.timeAcknowledged,
                    DateFormat.barChartMonth,
                  )}
                </MuteText>
              </div>
            </>
          ) : null,
      },
      {
        title: 'Warehouse',
        key: 'warehouse',
        align: 'left',
        width: '16%',
        render: (_, order) => {
          const provider = getProvider(order);

          return provider?.warehouseName ? (
            <div className="d-flex justify-content-start align-items-center">
              <Warehouse size={20} className="mr-2" color={theme.colors.grey} />
              <NameContainer>{provider?.warehouseName}</NameContainer>
            </div>
          ) : null;
        },
      },
      {
        title: (
          <ColumnHeaderTooltip
            label="Organization"
            helpText={tooltipTexts.organization}
          />
        ),
        key: 'organization',
        align: 'left',
        width: '18%',
        render: (_, order) => (
          <div className="d-flex justify-content-start align-items-center">
            <OrganisationAvatar image={order.logo} name={order.organizationName} />
            <NameContainer>&nbsp;{order.organizationName}</NameContainer>
          </div>
        ),
      },
      {
        title: <ColumnHeaderTooltip label="Customer" />,
        key: 'customer',
        align: 'left',
        width: '16%',
        render: (_, order) => (
          <div className="d-flex justify-content-start align-items-center">
            &nbsp;{order.shippingAddress?.name || <MuteText>N/A</MuteText>}
          </div>
        ),
      },
      {
        title: <ColumnHeaderTooltip label="Items" helpText={tooltipTexts.totalItems} />,
        key: 'total_items',
        align: 'center',
        width: '20%',
        render: (_, order) => {
          const totalItems = getTotalItems(order);

          return (
            <>
              <div className="d-flex justify-content-center align-items-center flex-column">
                <ItemInfoPopup
                  items={order.items}
                  position="top left"
                  total_items={CommonUtility.numberWithCommas(totalItems)}
                />
              </div>
            </>
          );
        },
      },
      ...(currentTab === 'pending-fulfillment'
        ? [
            {
              title: '',
              key: 'actions',
              align: 'center',
              fixed: 'right',
              width: 72,
              render: (_, order) => (
                <OrderRowActionsDropdown
                  items={[
                    {
                      key: 'fulfill',
                      label: 'Fulfill',
                      icon: <CheckSquareOffset />,
                    },
                  ]}
                  onAction={() => openPopup(order)}
                />
              ),
            },
          ]
        : []),
    ],
    [ageSortOrder, currentTab, openPopup],
  );

  const pausedOrderColumns = useMemo(
    () =>
      PausedOrderColumns({
        ageSortOrder,
        bulkSelectableOrders,
        onBulkSelectToggle,
        onBulkSelectHeaderToggle,
        onRecalculatePaused: recalculatePausedFulfillment,
        recalculatingOrderId,
        recalculatingPaused,
        selectedById,
      }),
    [
      ageSortOrder,
      bulkSelectableOrders,
      onBulkSelectToggle,
      onBulkSelectHeaderToggle,
      recalculatePausedFulfillment,
      recalculatingOrderId,
      recalculatingPaused,
      selectedById,
    ],
  );

  const orderColumns = isPausedTab ? pausedOrderColumns : standardOrderColumns;

  const tablePagination = useMemo(
    () => ({
      current: Number(tableFilter.page || 1),
      pageSize: Number(tableFilter.limit || 50),
      pageSizeOptions: CommonConstant.pageSizes.map(option => option.value),
      showSizeChanger: true,
      showTotal: total => `${CommonUtility.numberWithCommas(total)} orders`,
      total: totalData,
    }),
    [tableFilter.limit, tableFilter.page, totalData],
  );

  const handleTableChange = useCallback(
    (pagination, _, sorter, extra) => {
      if (extra?.action === 'sort') {
        const nextSorter = Array.isArray(sorter) ? sorter[0] : sorter;
        if (!nextSorter?.order) return;

        onSortChange({
          timeAcknowledged: getSortDirection(nextSorter.order),
        });
        return;
      }

      const nextPageSize = Number(pagination?.pageSize || 50);
      const currentPageSize = Number(tableFilter.limit || 50);
      if (nextPageSize !== currentPageSize) {
        filterChanged('limit', nextPageSize);
        return;
      }

      const nextPage = Number(pagination?.current || 1);
      if (nextPage !== Number(tableFilter.page || 1)) {
        pageChanged(nextPage);
      }
    },
    [filterChanged, onSortChange, pageChanged, tableFilter.limit, tableFilter.page],
  );

  const filters = useDebouncedMemo(
    () => {
      const query = Object.fromEntries(searchParams.entries());
      const temp = { ...query };
      Object.keys(temp).forEach(key => {
        const final = CommonUtility.tryParseJSONObject(temp[key]);
        if (final) {
          temp[key] = final;
        }
      });

      const defaultValues = {
        ...temp,
      };

      const arr = [
        {
          skip: currentTab !== 'pending-fulfillment',
          name: 'oos',
          label: 'Inventory Availability',
          type: 'select',
          defaultValue: defaultValues?.oos,
          options: [
            { label: 'Hide OOS Orders', value: 'false' },
            { label: 'Only OOS Orders', value: 'true' },
          ],
        },
        {
          skip: true,
          name: 'page',
          must: true,
          defaultValue: (defaultValues && defaultValues.page) || 1,
          ignoreDefault: 1,
        },
        {
          skip: true,
          name: 'limit',
          must: true,
          defaultValue:
            defaultValues && CommonUtility.isValidNumber(defaultValues.limit)
              ? Number(defaultValues.limit)
              : 50,
          ignoreDefault: 50,
        },
      ];

      return arr;
    },
    [searchParams, currentTab],
    500,
  );

  const setFilter = useCallback(
    next => {
      updateAllFilter({ ...next, page: 1 });
    },
    [updateAllFilter],
  );

  const didHydrateFromUrl = useRef(false);

  useEffect(() => {
    if (!isPausedTab) {
      setSelectedById({});
    }
  }, [isPausedTab]);

  useEffect(() => {
    if (didHydrateFromUrl.current) return;
    didHydrateFromUrl.current = true;

    const query = Object.fromEntries(searchParams.entries());
    const updates = { ...query };
    if (!updates.limit) {
      updates.limit = 50;
    }
    updateAllFilter(updates);
  }, [searchParams, updateAllFilter]);

  useEffect(() => {
    const urlObject = {};
    Object.keys(tableFilter)?.forEach(key => {
      if (key === 'oos' && currentTab !== 'pending-fulfillment') return;

      const value = tableFilter[key];
      const config = filters?.find(f => f.name === key);
      if (key === 'sort') {
        const sortValue = JSON.stringify(value);
        if (sortValue !== JSON.stringify(config?.ignoreDefault)) {
          urlObject[key] = sortValue;
        }
      } else if (
        value !== undefined &&
        value !== null &&
        value !== '' &&
        config?.ignoreDefault !== value
      ) {
        urlObject[key] = value;
      }
    });
    const nextSearch = new URLSearchParams(urlObject).toString();
    if (searchParams.toString() === nextSearch) return;

    navigate({ search: nextSearch }, { replace: true });
  }, [tableFilter, filters, navigate, searchParams]);

  useOrdersTabExtraContent(
    () => (
      <OrdersTabToolbar
        filters={filters}
        filter={tableFilter}
        setFilter={setFilter}
        filterChanged={filterChanged}
        onChangeCustomer={setCustomerId}
      />
    ),
    [filters, tableFilter, setFilter, filterChanged, setCustomerId],
  );

  return (
    <div className={isPausedTab && selectedCount > 0 ? 'pb-4 mb-4' : ''}>
      <Table
        columns={orderColumns}
        dataSource={orderData}
        loading={orderLoading}
        onChange={handleTableChange}
        pagination={tablePagination}
        rowKey={(order, index) => order?._id || `order-${index}`}
        scroll={{ x: 1100 }}
        size="small"
        tableLayout="fixed"
        style={{
          borderRadius: 6,
          border: `1px solid ${theme.colors.semanticBorder}`,
        }}
      />
      <FulfillOrderPopup
        open={!!orderToFulfill}
        onClose={closePopup}
        onConfirm={fulfillOrder}
        loading={loading}
      />
      {isPausedTab && selectedCount > 0 ? (
        <AlertFooterBar
          message={
            selectedCount === 1
              ? '1 paused order selected'
              : `${selectedCount} paused orders selected`
          }
          cancel={clearBulkSelection}
          cancelButtonText="Clear"
          save={() => recalculatePausedFulfillment()}
          saveButtonText="Recalculate Fulfillment"
          loading={recalculatingPaused}
        />
      ) : null}
    </div>
  );
}
